// Modules
import React, { useState, useRef, useEffect } from 'react';

// Components
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Styles
import styles from './Typeahead.module.scss';

export function mapToTypeaheadOptions(
  list,
  displayTextField,
  valueField = displayTextField
) {
  return list.map((element) => {
    return {
      displayText: element[displayTextField],
      value: element[valueField],
      ...element,
    };
  });
}

// documentation: https://vitusvet.atlassian.net/l/c/cwDVN8pW
const Typeahead = (props) => {
  const {
    value,
    selectedItem = null,
    options = [],
    placeholder = '',
    className = '',
    inputClassName = '',
    menuClassName = '',
    menuItemClassName = '',
    async = false,
    isLoading = false,
    scrollToBottom = false,
    displayClearButton = false,
    onChange = () => {},
    onItemSelected = () => {},
  } = props;

  const [isMenuOpen, setMenuOpen] = useState(false);

  const isItemSelected = selectedItem !== null;

  const handleChange = (e) => {
    onItemSelected(null);
    setMenuOpen(true);

    onChange(e);
  };

  const handleItemSelected = (selectedOption) => {
    onItemSelected(selectedOption);
  };

  const handleBlur = () => {
    setMenuOpen(false);
  };

  const handleFocus = () => {
    setMenuOpen(true);
  };

  const getOptions = () => {
    if (async) return options;

    // locally search for matches when async disabled
    return options.filter((opt) => {
      // this regex matches multiple words (in order)
      const re = new RegExp(
        value
          .split(' ')
          .map((s) => `(.*${s}.*)`)
          .join(''),
        'i'
      );
      return re.test(opt.displayText);
    });
  };

  const shouldShowMenu =
    !isLoading && !isItemSelected && isMenuOpen && options.length > 0;

  // logic for scrolling the bottom of the dropdown into view
  const scrollAnchorRef = useRef(null);
  useEffect(() => {
    if (!(shouldShowMenu && scrollToBottom)) return;

    // eslint-disable-next-line no-unused-expressions
    scrollAnchorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, [shouldShowMenu, scrollToBottom]);

  return (
    <div
      className={[className, styles.typeaheadFormGroup].join(' ')}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <input
        type='text'
        autoComplete='off'
        onChange={handleChange}
        className={[styles.typeaheadInput, inputClassName].join(' ')}
        disabled={displayClearButton && isItemSelected}
        value={value}
        placeholder={placeholder}
      />
      {isLoading ? (
        <div className={styles.spinnerBox}>
          <Spinner size='auto' />
        </div>
      ) : (
        displayClearButton &&
        isItemSelected && (
          <FontAwesomeIcon
            icon={faTimes}
            className={styles.clearButton}
            onClick={() => {
              onItemSelected(null);
              onChange({ target: { value: '' } });
            }}
          />
        )
      )}
      <div
        className={[
          menuClassName,
          styles.typeaheadListGroup,
          styles.listGroup,
        ].join(' ')}
        ref={scrollAnchorRef}
      >
        {shouldShowMenu &&
          getOptions().map((item, id) => (
            <button
              key={id}
              className={[
                menuItemClassName,
                styles.typeaheadListGroupItem,
                styles.listGroupItem,
              ].join(' ')}
              onMouseDown={() => handleItemSelected(item)}
            >
              <div className={styles.itemText}>{item.displayText}</div>
            </button>
          ))}
      </div>
    </div>
  );
};

export default Typeahead;
